import { BoundSelectField, Css, FormPageLayout, MultiSelectField } from "@homebound/beam";
import { type ObjectConfig, type ObjectState, required, useFormState } from "@homebound/form-state";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChangeEventBudgetPhase,
  ChangeEventType,
  ProjectReadyPlanConfigOrientation,
  type SaveProjectReadyPlanConfigsInput,
  Stage,
} from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import {
  ConfigurationErrorsTooltip,
  createCheckoutStepSections,
  createOptionCostById,
  createOptionGroups,
  createSelectedOptionIdByGroupId,
  getActiveOptions,
  getArchivedOptions,
  getDisabledOptionReasons,
  getOptionLabel,
  ReadyPlanOptionGroupSelect,
  toFormReadyPlanOptions,
  updateSelectedOptionIdsForGroup,
} from "src/routes/components/ProductOfferingConfigUtils";
import { type ProductOfferingConfigApplyParams } from "src/routes/routesDef";
import { createChangeEventsUrl } from "src/RouteUrls";
import { queriesResult } from "src/utils";
import {
  type ApplyProductOfferingConfig_ComputeResultFragment,
  type ApplyProductOfferingConfig_ProjectFragment,
  type ApplyProductOfferingConfig_SelectedQuery,
  type ApplyProductOfferingConfigMetaQuery,
  useApplyProductOfferingConfig_ComputeLazyQuery,
  useApplyProductOfferingConfig_SelectedQuery,
  useApplyProductOfferingConfigMetaQuery,
  useApplyProductOfferingConfigPageMetaQuery,
  useApplyProductOfferingConfigsMutation,
  useCreateProductOfferingConfigChangeEventMutation,
} from "./ApplyProductOfferingConfigPage.generated";

export function ApplyProductOfferingConfigPage() {
  const { configId, projectId } = useRouteParams<ProductOfferingConfigApplyParams>();
  const setupQuery = useApplyProductOfferingConfigPageMetaQuery({ variables: { projectId } });
  const configQuery = useApplyProductOfferingConfig_SelectedQuery({
    variables: {
      id: configId,
      skipCheckoutConfig: !configId.startsWith("ccfg:"),
      skipProductOfferingConfig: !configId.startsWith("poc:"),
      skipProjectReadyPlanConfig: !configId.startsWith("prpc:"),
    },
  });
  const selectedConfig = getProductOfferingSelection(configId, configQuery.data, setupQuery.data?.project);

  const metaDataQuery = useApplyProductOfferingConfigMetaQuery({
    variables: {
      productOfferingId: selectedConfig.productOfferingId ?? "",
      productOfferingVersionId: selectedConfig.productOfferingVersionId,
    },
    fetchPolicy: "no-cache",
    skip: setupQuery.loading || configQuery.loading || !selectedConfig.productOfferingId,
  });

  return queriesResult([setupQuery, configQuery, metaDataQuery] as const, {
    data: (setupData, configData, metaData) => (
      <ApplyProductOfferingConfigPageView
        metaData={metaData}
        configData={configData}
        productOfferingId={selectedConfig.productOfferingId!}
        initialReadyPlanOptionIds={selectedConfig.readyPlanOptionIds}
        project={setupData.project}
        changeEventReasons={setupData.changeEventReasons}
      />
    ),
  });
}

type ApplyOfferingConfigPageProps = {
  metaData: ApplyProductOfferingConfigMetaQuery;
  configData: ApplyProductOfferingConfig_SelectedQuery;
  productOfferingId: string;
  initialReadyPlanOptionIds: string[];
  project: ApplyProductOfferingConfig_ProjectFragment;
  changeEventReasons: { id: string; code: string }[];
};

type ReadyPlanOption = ApplyProductOfferingConfigMetaQuery["productOffering"]["options"][number];
type ReadyPlanOptionGroup = ReadyPlanOption["optionGroup"] & { readyPlanOptions: ReadyPlanOption[] };

function ApplyProductOfferingConfigPageView(props: ApplyOfferingConfigPageProps) {
  const navigate = useNavigate();
  const { metaData, configData, productOfferingId, project, changeEventReasons, initialReadyPlanOptionIds } = props;
  const constructionStage = project.stages.find((s) => s.stage.code === Stage.Construction);
  const archivedOptions = useMemo(
    () => getArchivedOptions(metaData.productOffering.options),
    [metaData.productOffering.options],
  );
  const allOptions = useMemo(
    () => getActiveOptions(metaData.productOffering.options),
    [metaData.productOffering.options],
  );

  const formState = useFormState({
    config: formConfig,
    init: { input: props, map: (queryResults) => mapConfigToForm(queryResults) },
  });
  const [applyProductOfferingConfigs] = useApplyProductOfferingConfigsMutation();
  const [createProductOfferingConfigChangeEvent] = useCreateProductOfferingConfigChangeEventMutation();
  const [computeAppliedConfig, computeQuery] = useApplyProductOfferingConfig_ComputeLazyQuery({
    fetchPolicy: "no-cache",
  });

  const [selectedOptionIds, setSelectedOptionIds] = useState<string[]>(initialReadyPlanOptionIds);
  const [appliedOptionIds, setAppliedOptionIds] = useState<string[]>([]);
  const [appliedComputeResult, setAppliedComputeResult] = useState<ApplyProductOfferingConfig_ComputeResultFragment>();

  const optionGroups = useMemo(() => createOptionGroups(allOptions), [allOptions]);
  const selectedOptionIdByGroupId = useMemo(
    () => createSelectedOptionIdByGroupId(allOptions, selectedOptionIds),
    [allOptions, selectedOptionIds],
  );
  const disabledOptionReasons = useMemo(
    () => getDisabledOptionReasons(allOptions, selectedOptionIds),
    [allOptions, selectedOptionIds],
  );
  const appliedOptionCostById = useMemo(
    () => (appliedComputeResult ? createOptionCostById(appliedComputeResult) : new Map<string, number>()),
    [appliedComputeResult],
  );
  const checkoutStepSections = useMemo(() => createCheckoutStepSections(optionGroups), [optionGroups]);
  const archivedOptionIds = useMemo(() => archivedOptions.map((option) => option.id), [archivedOptions]);
  const selectedArchivedOptionIds = useMemo(
    () => selectedOptionIds.filter((optionId) => archivedOptionIds.includes(optionId)),
    [archivedOptionIds, selectedOptionIds],
  );
  const appliedArchivedOptionIds = useMemo(
    () => appliedOptionIds.filter((optionId) => archivedOptionIds.includes(optionId)),
    [appliedOptionIds, archivedOptionIds],
  );
  const showArchivedSection =
    archivedOptions.nonEmpty && (selectedArchivedOptionIds.nonEmpty || appliedArchivedOptionIds.nonEmpty);

  const loadOptionIds = useCallback(
    (optionIds: string[]) => {
      setSelectedOptionIds(optionIds);
      formState.readyPlanOptions.set(toFormReadyPlanOptions(optionIds));
    },
    [formState],
  );

  const updateAppliedConfig = useCallback(
    async (optionIds: string[], originalOptionIds?: string[]) => {
      const { data } = await computeAppliedConfig({
        variables: {
          readyPlanId: productOfferingId,
          readyPlanOptionIds: optionIds,
          originalReadyPlanOptionIds: originalOptionIds?.nonEmpty ? originalOptionIds : undefined,
        },
      });
      const result = data?.computeReadyPlanCost;
      if (!result) return;

      // computeReadyPlanCost can omit archived options, but keep them selected until the user explicitly removes them.
      const submittedArchivedOptionIds = optionIds.filter((optionId) => archivedOptionIds.includes(optionId));
      const resolvedOptionIds = [
        ...result.readyPlanOptions.map((option) => option.id),
        ...submittedArchivedOptionIds,
      ].unique();
      loadOptionIds(resolvedOptionIds);
      setAppliedOptionIds(resolvedOptionIds);
      setAppliedComputeResult(result);
    },
    [archivedOptionIds, computeAppliedConfig, loadOptionIds, productOfferingId],
  );

  // Only hydrate from the source config once; duplicate effect runs would re-call compute and could reset user edits.
  const didUpdateInitialConfig = useRef(false);
  useEffect(() => {
    if (didUpdateInitialConfig.current) return;
    didUpdateInitialConfig.current = true;
    void updateAppliedConfig(initialReadyPlanOptionIds);
  }, [initialReadyPlanOptionIds, updateAppliedConfig]);

  const handleOptionGroupSelect = useCallback(
    (optionGroup: ReadyPlanOptionGroup, optionId: string | undefined) => {
      const nextOptionIds = updateSelectedOptionIdsForGroup(allOptions, selectedOptionIds, optionGroup.id, optionId);
      loadOptionIds(nextOptionIds);
    },
    [allOptions, loadOptionIds, selectedOptionIds],
  );
  const handleArchivedOptionsSelect = useCallback(
    (archivedOptionIds: string[]) => {
      const archivedOptionIdSet = new Set(archivedOptions.map((option) => option.id));
      loadOptionIds([
        ...selectedOptionIds.filter((optionId) => !archivedOptionIdSet.has(optionId)),
        ...archivedOptionIds,
      ]);
    },
    [archivedOptions, loadOptionIds, selectedOptionIds],
  );

  const hasUnappliedSelections = !selectedOptionIds.hasSameElements(appliedOptionIds);
  const configurationErrors = appliedComputeResult?.configurationErrors ?? [];
  const disableApply =
    (computeQuery.loading && "Updating selections") ||
    (!appliedComputeResult && "Update selections before applying") ||
    (hasUnappliedSelections && "Update selections before applying") ||
    (configurationErrors.nonEmpty && "Resolve configuration errors before applying") ||
    // Validate this separately from configureation errors since we add the archived options back in
    // once we have auto-fixed them to ensure the users are actively aware they are removing these options
    (selectedArchivedOptionIds.nonEmpty && "Remove archived options before applying");
  const disableUpdate =
    (computeQuery.loading && "Updating selections") ||
    (!!appliedComputeResult && !hasUnappliedSelections && "Selections are already up to date");
  const cancelLabel = configurationErrors.nonEmpty ? (
    <>
      <span data-testid="configErrors">{configurationErrors.length} Configuration Errors</span> · Cancel
    </>
  ) : (
    "Cancel"
  );

  const redirectRoute = createChangeEventsUrl(props.project.id);

  return (
    <FormPageLayout
      formSections={[
        {
          title: "Details",
          rows: [
            {
              reactNodeSubtitle:
                "Select options, click Update to resolve the configuration, then Apply the updated configuration to the project.",
            },
            {
              reactNode: (
                <div css={Css.df.fdc.gap2.$}>
                  <BoundSelectField
                    label="Orientation"
                    field={formState.orientation}
                    options={orientationOptions}
                    getOptionValue={(o) => o.code}
                    getOptionLabel={(o) => o.name}
                  />
                  <div css={Css.df.aic.gap1.flexWrap("wrap").$}>
                    {hasUnappliedSelections && (
                      <span css={Css.sm.gray700.$}>Selections have not been updated yet.</span>
                    )}
                  </div>
                  {computeQuery.error && (
                    <div css={Css.sm.red600.$}>Failed to update: {computeQuery.error.message}</div>
                  )}
                </div>
              ),
            },
          ],
        },
        ...checkoutStepSections.map((section) => ({
          title: section.title,
          rows: [
            {
              reactNode: (
                <div css={Css.df.fdc.gap2.$}>
                  {section.optionGroups.map((optionGroup) => (
                    <ReadyPlanOptionGroupSelect
                      key={optionGroup.id}
                      optionGroup={optionGroup}
                      value={selectedOptionIdByGroupId.get(optionGroup.id)}
                      selectedOptionIds={selectedOptionIds}
                      appliedOptionIds={appliedOptionIds}
                      appliedOptionCostById={appliedOptionCostById}
                      disabledOptionReasons={disabledOptionReasons}
                      onSelect={(optionId) => handleOptionGroupSelect(optionGroup, optionId)}
                    />
                  ))}
                </div>
              ),
            },
          ],
        })),
        ...(showArchivedSection
          ? [
              {
                title: "Archived",
                rows: [
                  {
                    reactNode: (
                      <MultiSelectField
                        data-testid="archivedOptions"
                        label="Archived Options"
                        values={selectedArchivedOptionIds}
                        options={archivedOptions}
                        getOptionValue={(option) => option.id}
                        getOptionLabel={(option) => getOptionLabel(option, appliedOptionIds, appliedOptionCostById)}
                        onSelect={handleArchivedOptionsSelect}
                        nothingSelectedText="None"
                        autoSort={false}
                      />
                    ),
                  },
                ],
              },
            ]
          : []),
      ]}
      breadCrumb={{ label: "Change Events / Create New", href: "" }}
      formState={formState}
      pageTitle="Create Change Event"
      submitAction={{
        label: "Apply",
        onClick: async () => {
          if (constructionStage?.hasSignedContract) {
            await createProductOfferingConfigChangeEvent({
              variables: {
                input: {
                  projectStageId: constructionStage?.id,
                  readyPlanId: productOfferingId,
                  originalReadyPlanId: project.readyPlanConfig?.readyPlan?.id,
                  originalReadyPlanOptions: project.readyPlanConfig?.readyPlanOptions.map((rpo) => ({
                    readyPlanOptionId: rpo.id,
                  })),
                  proposedReadyPlanOptions: appliedOptionIds.map((readyPlanOptionId) => ({ readyPlanOptionId })),
                  proposedOrientation: formState.value.orientation,
                  checkoutConfigId: configData.checkoutConfig?.id,
                  title: `Change Configuration of POF ${productOfferingId} on ${project.name}`,
                  reasonId: changeEventReasons.find((reason) => reason.code === "RFP_EXECUTION")?.id ?? "cer:1",
                  type: ChangeEventType.External,
                  budgetPhase: ChangeEventBudgetPhase.Prebid,
                },
              },
            });
          } else {
            await applyProductOfferingConfigs({
              variables: {
                input: mapFormToApplyProductConfigConfigInput(
                  project.readyPlanConfig?.id!,
                  formState,
                  appliedOptionIds,
                ),
              },
            });
          }

          return navigate(redirectRoute);
        },
        disabled: disableApply,
      }}
      tertiaryAction={{
        label: cancelLabel,
        onClick: redirectRoute,
        tooltip: configurationErrors.nonEmpty ? (
          <ConfigurationErrorsTooltip configurationErrors={configurationErrors} />
        ) : undefined,
      }}
      cancelAction={{
        label: computeQuery.loading ? "Updating..." : "Update",
        onClick: () => void updateAppliedConfig(selectedOptionIds, appliedOptionIds),
        disabled: disableUpdate,
      }}
    />
  );
}

type ApplyProductOfferingConfigForm = {
  productOfferingId: string | null | undefined;
  readyPlanOptions: { readyPlanOptionId: string | null | undefined }[];
  orientation: ProjectReadyPlanConfigOrientation | null | undefined;
};

const orientationOptions = [
  { code: ProjectReadyPlanConfigOrientation.Left, name: "Left" },
  { code: ProjectReadyPlanConfigOrientation.Right, name: "Right" },
];

const formConfig: ObjectConfig<ApplyProductOfferingConfigForm> = {
  productOfferingId: { type: "value", rules: [required] },
  readyPlanOptions: { type: "list", config: { readyPlanOptionId: { type: "value" } } },
  orientation: { type: "value" },
};

function mapConfigToForm(queryResults: ApplyOfferingConfigPageProps): ApplyProductOfferingConfigForm {
  const { configData, project, productOfferingId, initialReadyPlanOptionIds } = queryResults;
  return {
    productOfferingId,
    readyPlanOptions: toFormReadyPlanOptions(initialReadyPlanOptionIds),
    orientation: getInitialOrientation(configData, project),
  };
}

function mapFormToApplyProductConfigConfigInput(
  structureProjectConfigId: string,
  formState: ObjectState<ApplyProductOfferingConfigForm>,
  readyPlanOptionIds: string[],
): SaveProjectReadyPlanConfigsInput {
  const { productOfferingId, orientation } = formState.value;
  return {
    projectReadyPlanConfigs: [
      {
        id: structureProjectConfigId,
        readyPlanId: productOfferingId,
        orientation,
        options: readyPlanOptionIds.map((readyPlanOptionId) => ({ readyPlanOptionId })),
      },
    ],
    overwriteProgramDataWithComputed: true,
  };
}

type ProductOfferingSelection = {
  productOfferingId?: string;
  productOfferingVersionId?: string;
  readyPlanOptionIds: string[];
};

/** Finds the source product offering and option ids for the selected config id. */
function getProductOfferingSelection(
  configId: string,
  configData: ApplyProductOfferingConfig_SelectedQuery | undefined,
  project: ApplyProductOfferingConfig_ProjectFragment | undefined,
): ProductOfferingSelection {
  if (configId.startsWith("poc:")) {
    const productOfferingConfig = configData?.productOfferingConfig;
    return {
      productOfferingId: productOfferingConfig?.productOffering.id,
      productOfferingVersionId: productOfferingConfig?.productOffering.aggregateCheckout?.id,
      readyPlanOptionIds: productOfferingConfig?.readyPlanOptions.map((rpo) => rpo.id) ?? [],
    };
  }

  if (configId.startsWith("ccfg:")) {
    const checkoutConfig = configData?.checkoutConfig;
    if (!checkoutConfig || !project) return { readyPlanOptionIds: [] };

    const isAdu = project.__typename === "Structure" && project.isAdu;
    const productOfferingId = isAdu ? checkoutConfig.aduProductOffering?.id : checkoutConfig.productOffering?.id;
    return {
      productOfferingId,
      productOfferingVersionId: isAdu
        ? checkoutConfig.aduProductOffering?.aggregateCheckout?.id
        : checkoutConfig.productOffering?.aggregateCheckout?.id,
      readyPlanOptionIds:
        checkoutConfig.optionGroups
          .filter((og) => og.selectedOption?.option.readyPlan.id === productOfferingId)
          .map((og) => og.selectedOption?.option.id)
          .compact() ?? [],
    };
  }

  const projectReadyPlanConfig = configData?.projectReadyPlanConfig;
  const readyPlan = projectReadyPlanConfig?.readyPlan;
  return {
    productOfferingId: projectReadyPlanConfig?.readyPlan?.id,
    productOfferingVersionId: readyPlan?.__typename === "ProductOffering" ? readyPlan.aggregateCheckout?.id : undefined,
    readyPlanOptionIds: projectReadyPlanConfig?.readyPlanOptions.map((rpo) => rpo.id) ?? [],
  };
}

/** Determines the orientation that should be shown on initial load. */
function getInitialOrientation(
  configData: ApplyProductOfferingConfig_SelectedQuery,
  project: ApplyProductOfferingConfig_ProjectFragment,
): ProjectReadyPlanConfigOrientation {
  if (configData.checkoutConfig) {
    return configData.checkoutConfig.isReversed
      ? ProjectReadyPlanConfigOrientation.Left
      : ProjectReadyPlanConfigOrientation.Right;
  }
  if (configData.projectReadyPlanConfig) return configData.projectReadyPlanConfig.orientation;
  return project.readyPlanConfig?.orientation ?? ProjectReadyPlanConfigOrientation.Right;
}
